"use client";

import { useEffect } from "react";
import type { PushNudgeSurface } from "@/db/schema/push-nudge";
import { type PushNudgeStage, usePushNudge } from "../hooks/use-push-nudge";
import type { PushNudgeParams } from "../lib/push-nudge-copy";
import { PushNudgeCard } from "./push-nudge-card.client";
import { PushNudgeConfirmation } from "./push-nudge-confirmation.client";
import { PushNudgeRescue } from "./push-nudge-rescue.client";
import { PushNudgeSheet } from "./push-nudge-sheet.client";

/**
 * Which shell each surface wears.
 *
 * The flow picks the variant, never the other way around — so this mapping is
 * data, and adding a surface is a decision about *where* it appears rather than
 * an invitation to invent a new shape for it.
 *
 * Sheets are spent only on celebration moments, where an interruption reads as
 * part of the good news. Everything else is a card in the page's own content.
 */
const VARIANT_BY_SURFACE: Record<PushNudgeSurface, "sheet" | "card"> = {
  event_booking_success: "sheet",
  activity_session_joined: "sheet",
  event_waitlist_joined: "card",
  tier_join_request_sent: "card",
  // Every surface below is a card, and none of them is a close call. Each one
  // catches the user mid-task — reading a thread, scanning who owes what,
  // holding a share sheet they just came back from — and a modal there
  // interrupts the very thing that made the moment worth asking in.
  chat_message_sent: "card",
  connection_request_sent: "card",
  activity_session_hosted: "card",
  session_split_open: "card",
  referral_link_shared: "card",
  push_rescue: "card",
};

export type PushNudgeProps = {
  surface: PushNudgeSurface;
  params?: PushNudgeParams;
  /** What the user will receive, for the post-grant confirmation. */
  confirmationScopes?: string[];
  className?: string;
  /**
   * Told whenever the nudge changes stage, for hosts that float it over their
   * content and must retire it themselves (the confirmation otherwise stays
   * for as long as it is mounted).
   */
  onStageChange?: (stage: PushNudgeStage) => void;
};

/**
 * The single call site a screen needs.
 *
 * Renders nothing at all unless this user, on this device, at this moment, may
 * be asked — every cap, the OS dialog budget and the native-only rule live
 * behind `usePushNudge`, so a caller cannot accidentally bypass them by
 * forgetting a condition.
 */
export function PushNudge({
  surface,
  params,
  confirmationScopes,
  className,
  onStageChange,
}: PushNudgeProps) {
  const { stage, copy, isBusy, act, dismiss } = usePushNudge(surface, params);

  useEffect(() => {
    onStageChange?.(stage);
  }, [stage, onStageChange]);

  if (stage === "hidden") return null;

  if (stage === "confirmed") {
    return (
      <PushNudgeConfirmation
        scopes={confirmationScopes ?? []}
        className={className}
      />
    );
  }

  if (stage === "rescue") {
    return (
      <PushNudgeRescue
        copy={copy}
        onPrimary={() => void act()}
        onDismiss={dismiss}
        className={className}
      />
    );
  }

  if (VARIANT_BY_SURFACE[surface] === "sheet") {
    return (
      <PushNudgeSheet
        open
        copy={copy}
        isBusy={isBusy}
        onPrimary={() => void act()}
        onDismiss={dismiss}
      />
    );
  }

  return (
    <PushNudgeCard
      copy={copy}
      isBusy={isBusy}
      onPrimary={() => void act()}
      onDismiss={dismiss}
      className={className}
    />
  );
}
